import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Constants } from '../../core/helpers/constants';

export type ThemeMode = 'light' | 'dark' | 'glassy';

@Injectable({ providedIn: 'root' })
export class ThemeModeService {
  private readonly storageKey = Constants.LS_THEME_MODE;
  private readonly mode$ = new BehaviorSubject<ThemeMode>('light');
  private initialized = false;

  watch(): Observable<ThemeMode> {
    this.ensureInitialized();
    return this.mode$.asObservable();
  }

  getSnapshot(): ThemeMode {
    this.ensureInitialized();
    return this.mode$.value;
  }

  initialize(): void {
    if (this.initialized) {
      this.applyMode(this.mode$.value);
      return;
    }

    const storedMode = this.readStoredMode();
    this.initialized = true;
    this.mode$.next(storedMode);
    this.applyMode(storedMode);
  }

  setMode(mode: ThemeMode): void {
    this.ensureInitialized();
    if (this.mode$.value === mode) {
      this.applyMode(mode);
      return;
    }

    this.mode$.next(mode);
    this.persistMode(mode);
    this.applyMode(mode);
  }

  toggleMode(): ThemeMode {
    const nextMode: ThemeMode = this.getSnapshot() === 'light' ? 'dark' : 'light';
    this.setMode(nextMode);
    return nextMode;
  }

  private ensureInitialized(): void {
    if (!this.initialized) {
      this.initialize();
    }
  }

  private readStoredMode(): ThemeMode {
    const stored = (localStorage.getItem(this.storageKey) || '').trim().toLowerCase();
    return stored === 'dark' || stored === 'glassy' ? stored : 'light';
  }

  private persistMode(mode: ThemeMode): void {
    localStorage.setItem(this.storageKey, mode);
  }

  private applyMode(mode: ThemeMode): void {
    document.documentElement.setAttribute('data-theme', mode);
    document.body.setAttribute('data-theme', mode);
    document.body.setAttribute('data-layout-mode', mode === 'glassy' ? 'light' : mode);
    document.body.setAttribute('data-bs-theme', mode);
  }
}